import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import qs from 'qs';
import { showErrorToast, showSuccessToast } from '../../../utilities/notificationUtils';
import SignUpButtonOptions from '../components/SignUpButtons';
import SignUpWithEmail from '../components/SignUpWithEmail';
import SignUpWithGoogle from '../components/SignUpWithGoogle';
import LoginFlow from '../../Login/LoginFlow';
import ForgotPasswordFlow from '../../ForgotPassword/ForgotPasswordFlow';
import VerificationCode from '../../../components/VerificationCode';
import {
  LoginError,
  GoogleAuthError,
  SignUpProps,
  SignUpError,
  PatientState,
  Patient,
  AppleAuthError
} from 'kb-shared';
import * as redux from '../../../redux';
import { Lab } from '../components/SignUpWithEmail';
import SignUpWithApple from '../components/SignUpWithApple';

const { signUp, signUpGoogleUser, login, clearGoogleAuthError } = redux.patient;

const Container = styled.div`
  flex: 1;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export type CreateSubStep =
  | 'choose_signup_option'
  | 'email'
  | 'google_attributes'
  | 'apple_attributes'
  | 'login'
  | 'forgot_password'
  | 'verification_code';

type Props = RouteComponentProps & {
  mobileLayout: boolean;
  lab: Lab | null;
  userConfirmationState: string | null;
  patient: Patient;
  isLoggedIn: boolean;
  loading: boolean;
  signUpError: SignUpError | null;
  googleUserId?: number;
  googleAuthError: GoogleAuthError | null;
  appleAuthError: AppleAuthError | null;
  onAuthentication: () => void;
  signUpUser: (props: SignUpProps) => void;
  signUpGoogleUser: (props: SignUpProps) => void;
  clearGoogleError: () => void;
  login: (email: string, password: string) => void;
  onChangeSubstep: (step: CreateSubStep) => void;
};

type State = {
  currentStep: string;
  formType: string;
  userEmail: string | null;
  userPassword: string | null;
};

class CreateAccount extends React.Component<Props, State> {
  state: State = {
    currentStep: 'choose_signup_option',
    formType: 'request_code',
    userEmail: null,
    userPassword: null
  };

  getEventFromUrl() {
    const event = qs.parse(this.props.history.location.search).event;
    if (typeof event === 'string') {
      return event;
    }
  }

  componentDidMount() {
    this.handleGoogleAuthError(true);
  }

  componentDidUpdate(prevProps: Props) {
    const {
      googleUserId: nextGoogleUserId,
      userConfirmationState: confirmationState,
      googleAuthError,
      appleAuthError
    } = this.props;

    const { googleUserId: prevGoogleUserId, googleAuthError: prevGoogleAuthError } = prevProps;

    // Google auth is handled via redux. A handler is provided to the google
    // auth setup on app setup, so respond to successful google sign in here, rather than a direct callback
    if (nextGoogleUserId && prevGoogleUserId !== nextGoogleUserId) {
      // If we have a googleUserId and the confirmation state is 'unconfirmed' they have just signed up with google
      if (nextGoogleUserId && (!confirmationState || confirmationState === 'unconfirmed')) {
        if (window.analytics) {
          window.analytics.page('Google Sign Up Started');
        }
        // Redirect to Google Sign Up details
        this.updateStep('google_attributes');
      }
    }

    if (googleAuthError && googleAuthError !== prevGoogleAuthError) {
      this.handleGoogleAuthError();
    }

    if (appleAuthError && appleAuthError !== prevProps.appleAuthError) {
      this.handleAppleAuthError(appleAuthError);
    }

    const { patient } = this.props;
    const { currentStep } = this.state;
    if (
      patient.apple?.token &&
      patient.email &&
      currentStep !== 'apple_attributes' &&
      appleAuthError?.type !== 'UserAlreadyExistsAsEmail'
    ) {
      this.setState({
        currentStep: 'apple_attributes'
      });
    }
  }

  handleAppleAuthError(error: AppleAuthError) {
    if (error.type == 'UserAlreadyExistsAsEmail') {
      showErrorToast(
        'You already have an account using your email address and password. Please login'
      );
      this.setState({
        currentStep: 'choose_signup_option'
      });
    }
  }

  handleGoogleAuthError(showMessage?: boolean) {
    const { googleAuthError, history } = this.props;
    if (!googleAuthError) return;
    const { type } = googleAuthError;
    if (type === 'UserHasNotSignedUp') {
      if (showMessage) {
        showSuccessToast('Please finish signing up with your Google Account.');
      }
      this.setState({
        currentStep: 'google_attributes'
      });
      // Clear error
      this.props.clearGoogleError();
    } else if (type === 'UserAlreadyExistsAsEmail') {
      showErrorToast(
        'You already have an account using your email address and password. Please login'
      );
      setTimeout(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const target = urlParams.get('target');
        const membershipId = urlParams.get('membership_id');
        const enterpriseMembership = urlParams.get('enterprise_membership');
        let newLocation = '/login';
        if (target) {
          newLocation = `/login?target=${encodeURIComponent(target)}`;
          if (membershipId) {
            // hacky fix because membership id is not being parsed as a part of the target param
            newLocation = `/login?target=${encodeURIComponent(
              `${target}&membership_id=${membershipId}`
            )}`;
          } else if (enterpriseMembership) {
            newLocation = `/login?target=${encodeURIComponent(
              `${target}&enterprise_membership=true`
            )}`;
          }
        }
        history.replace(newLocation);
      });
    }
  }

  updateStep(step: CreateSubStep) {
    this.setState(
      {
        currentStep: step
      },
      () => {
        this.props.onChangeSubstep(step);
      }
    );
  }

  renderCurrentStep() {
    switch (this.state.currentStep) {
      case 'choose_signup_option':
        return this.renderSignUpOptions();
      case 'email':
        return this.renderEmailSignupForm();
      case 'google_attributes':
        return this.renderGoogleAttributes();
      case 'apple_attributes':
        return this.renderAppleAttributes();
      case 'login':
        return this.renderLoginScreen();
      case 'forgot_password':
        return this.renderForgotPassword();
      case 'verification_code':
        return this.renderVerificationCode();
      default:
        return null;
    }
  }

  renderSignUpOptions() {
    return (
      <SignUpButtonOptions
        onSignUpWithEmail={() => {
          this.updateStep('email');
        }}
        onSignUpWithApple={() => {
          this.updateStep('apple_attributes');
        }}
        onHaveAnAccount={() => {
          this.updateStep('login');
        }}
      />
    );
  }

  renderEmailSignupForm() {
    const { userConfirmationState, loading, signUpError, mobileLayout } = this.props;
    return (
      <SignUpWithEmail
        preselectedLab={this.props.lab}
        signUpUser={(props: SignUpProps) => {
          this.props.signUpUser({ ...props, event: this.getEventFromUrl() });
          const { email, password } = props;
          this.setState({
            userEmail: email,
            userPassword: password || null
          });
        }}
        includeMembershipTerms={false}
        userConfirmationState={userConfirmationState}
        loading={loading}
        signUpError={signUpError}
        onError={(error: string) => {
          showErrorToast(error);
        }}
        onUserAlreadyExists={() => {
          // If user attempts to create an account w email that already exists, show login.
          this.updateStep('login');
        }}
        onSuccess={(state: string) => {
          if (state === 'unconfirmed') {
            this.updateStep('verification_code');
          } else {
            this.props.onAuthentication();
          }
        }}
        mobile={mobileLayout}
      />
    );
  }

  renderAppleAttributes() {
    return (
      <SignUpWithApple preselectedLab={this.props.lab} onSuccess={this.props.onAuthentication} />
    );
  }

  renderGoogleAttributes() {
    const {
      patient,
      isLoggedIn,
      userConfirmationState,
      loading,
      googleAuthError,
      signUpError
    } = this.props;

    return (
      <SignUpWithGoogle
        patient={patient}
        isLoggedIn={isLoggedIn}
        preselectedLab={this.props.lab}
        signUpUser={(props: SignUpProps) => {
          this.props.signUpGoogleUser({ ...props, event: this.getEventFromUrl() });
        }}
        userConfirmationState={userConfirmationState}
        loading={loading}
        googleAuthError={googleAuthError}
        signUpError={signUpError}
        onError={(error: string) => {
          showErrorToast(error);
        }}
        onSuccess={() => {
          this.props.onAuthentication();
        }}
      />
    );
  }

  renderLoginScreen() {
    return (
      <LoginFlow
        mobile={this.props.mobileLayout}
        showSignUpBoxMobile={false}
        onLogin={() => {
          this.props.onAuthentication();
        }}
        onError={(error: LoginError) => {
          if (error.type === 'Unconfirmed') {
            setTimeout(() => {
              // If user is unconfirmed, show the confirmation code component
              this.updateStep('verification_code');
            }, 5000);
          }
        }}
        onForgotPassword={() => {
          this.updateStep('forgot_password');
        }}
      />
    );
  }

  renderForgotPassword() {
    return (
      <ForgotPasswordFlow
        onSuccessfulReset={() => {
          this.updateStep('login');
        }}
        onGoBack={() => {
          this.updateStep('login');
        }}
        handleUnconfirmed={email => {
          this.props.history.replace('/verification-code', { email: email });
        }}
        // @ts-ignore
        formType={this.state.formType}
        updateFormType={formType => this.setState({ formType })}
      />
    );
  }

  renderVerificationCode() {
    return (
      <VerificationCode
        email={this.state.userEmail}
        onSuccess={() => {
          if (window.analytics) {
            window.analytics.track('Account Verification Succeeded');
          }
          const { userEmail, userPassword } = this.state;
          if (userEmail && userPassword) {
            this.props.login(userEmail, userPassword);
            this.props.onAuthentication();
            return;
          }
          // This is the final step before, confirm
          showSuccessToast('Thank you for verifying your account! You may now log in.');
          this.updateStep('login');
        }}
        onError={(error: string) => {
          if (window.analytics) {
            window.analytics.track('Account Verification Failed');
          }
          showErrorToast(error);
        }}
      />
    );
  }

  render() {
    return (
      <Container>
        <Content>{this.renderCurrentStep()}</Content>
      </Container>
    );
  }
}

const mapStateToProps = ({ patient }: { patient: PatientState }) => ({
  userConfirmationState: patient.confirmationState,
  patient: patient.patient,
  isLoggedIn: patient.isLoggedIn,
  loading: patient.loading,
  signUpError: patient.signUpError,
  googleUserId: patient.patient.google && patient.patient.google.id,
  googleAuthError: patient.googleAuthError,
  appleAuthError: patient.appleAuthError
});

const mapDispatchToProps = (dispatch: Function) => ({
  signUpUser: (props: SignUpProps) => dispatch(signUp(props)),
  signUpGoogleUser: (props: SignUpProps) => dispatch(signUpGoogleUser(props)),
  login: (email: string, password: string) => dispatch(login(email, password)),
  clearGoogleError: () => dispatch(clearGoogleAuthError())
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CreateAccount));
